import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { CheckCircle2, Download, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useUpdater } from '@/store/updater';
import { setSkippedVersion, type UpdateEvent } from '@/lib/updater';

/**
 * Modal that appears on desktop when an update is available + the user
 * either:
 *   (a) hasn't seen this version yet (first detect this app session); OR
 *   (b) clicked the IconRail download badge to re-open it.
 */

type Phase = 'idle' | 'downloading' | 'installing' | 'done' | 'error';

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

async function closeAppWindow(): Promise<void> {
  // Best-effort relaunch helper. Tauri's `getCurrentWindow().close()` kills
  // this window which (with one main window) exits the process. User then
  // re-launches manually — the on-disk binary is already the new version.
  try {
    const { getCurrentWindow } = await import('@tauri-apps/api/window');
    await getCurrentWindow().close();
  } catch {
    // Web build or plugin missing — fall back to a hard nav so something
    // visible happens. The user can close manually.
    window.location.href = 'about:blank';
  }
}

export function UpdateDialog() {
  const update = useUpdater((s) => s.update);
  const open = useUpdater((s) => s.dialogOpen);
  const close = useUpdater((s) => s.closeDialog);
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [received, setReceived] = useState(0);
  const [total, setTotal] = useState<number | null>(null);

  if (!update) return null;

  function onEvent(e: UpdateEvent) {
    switch (e.kind) {
      case 'download-started':
        setPhase('downloading');
        setReceived(0);
        setTotal(e.total);
        break;
      case 'download-progress':
        setReceived((r) => r + e.delta);
        break;
      case 'download-finished':
        // dpkg/pkexec phase begins now. On Linux this is when the polkit
        // password dialog opens — explicitly call it out so the user knows
        // to look for it (it can spawn behind the Console window).
        setPhase('installing');
        break;
      case 'install-finished':
        setPhase('done');
        break;
    }
  }

  async function install() {
    if (!update) return;
    setError(null);
    setPhase('downloading');
    try {
      await update.install(onEvent);
      // If install() returns and onEvent didn't fire install-finished
      // (older Tauri builds), promote to done anyway.
      setPhase((p) => (p === 'done' ? p : 'done'));
    } catch (e: any) {
      setError(e?.message ?? String(e));
      setPhase('error');
    }
  }

  function skipThisVersion() {
    if (!update) return;
    setSkippedVersion(update.version);
    close();
  }

  const busy = phase === 'downloading' || phase === 'installing';
  const pct =
    phase === 'downloading' && total && total > 0
      ? Math.min(100, Math.round((received / total) * 100))
      : null;

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(o) => {
        if (busy) return; // refuse to dismiss mid-install
        if (!o) close();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-[440px] max-h-[calc(100vh-2rem)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-border-default bg-surface-1 p-5 shadow-2xl"
        >
          <div className="mb-3 flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="m-0 flex items-center gap-2 text-base font-semibold">
                {phase === 'done' ? (
                  <CheckCircle2 className="h-4 w-4 text-status-good" />
                ) : (
                  <Download className="h-4 w-4 text-accent" />
                )}
                {phase === 'done' ? 'Update installed' : 'Update available'}
              </Dialog.Title>
              <p className="mt-1 font-mono text-[11px] text-fg-muted">
                Console {update.version}
                {update.pubDate && ` · ${new Date(update.pubDate).toLocaleDateString()}`}
              </p>
            </div>
            {!busy && (
              <Dialog.Close asChild>
                <button className="text-fg-muted hover:text-fg-primary" aria-label="Close">
                  <X className="h-4 w-4" />
                </button>
              </Dialog.Close>
            )}
          </div>

          <Dialog.Description className="mb-4 text-[13px] leading-relaxed text-fg-secondary">
            {phase === 'done' ? (
              <>
                Console <span className="font-mono">{update.version}</span> is installed. Restart
                Console to start using it — the running window still has the previous version
                loaded.
              </>
            ) : phase === 'installing' ? (
              <>
                Installing… an authentication prompt may have opened behind this window — check
                your taskbar / Activities and enter your password when it appears.
              </>
            ) : phase === 'downloading' ? (
              total ? `Downloading ${fmtBytes(received)} of ${fmtBytes(total)}${pct !== null ? ` (${pct}%)` : ''}…`
                : `Downloading ${fmtBytes(received)}…`
            ) : update.notes ? (
              update.notes
            ) : (
              'New version of Console is available. The update is signed and verified before install.'
            )}
          </Dialog.Description>

          {phase === 'downloading' && pct !== null && (
            <div className="mb-3 h-1.5 w-full overflow-hidden rounded-full bg-surface-2">
              <div
                className="h-full bg-accent transition-[width] duration-150"
                style={{ width: `${pct}%` }}
              />
            </div>
          )}

          {error && (
            <div className="mb-3 rounded border border-status-critical/40 bg-status-critical/10 p-2 text-xs text-status-critical">
              {error}
            </div>
          )}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            {phase === 'done' ? (
              <>
                <Button variant="ghost" size="sm" onClick={close}>
                  Restart later
                </Button>
                <Button variant="primary" size="sm" onClick={closeAppWindow}>
                  Close & restart
                </Button>
              </>
            ) : phase === 'error' ? (
              <>
                <Button variant="ghost" size="sm" onClick={close}>
                  Dismiss
                </Button>
                <Button variant="primary" size="sm" onClick={install}>
                  <Download className="h-3.5 w-3.5" /> Retry
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" size="sm" disabled={busy} onClick={skipThisVersion}>
                  Skip this version
                </Button>
                <Button variant="ghost" size="sm" disabled={busy} onClick={close}>
                  Later
                </Button>
                <Button variant="primary" size="sm" disabled={busy} onClick={install}>
                  {busy ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      {phase === 'installing' ? 'Installing…' : 'Downloading…'}
                    </>
                  ) : (
                    <>
                      <Download className="h-3.5 w-3.5" />
                      Install now
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
